import { create } from 'zustand';
import { getConcatenatedUrlParts, getCurrentUser } from "../utils/helpers";
import { toast } from 'sonner';

import { getStripHtmlTagsStr } from '../utils/helpers';
import { generateFingerprint } from '../utils/fingerprint';
import { ARTICLE_LIMIT_COUNT } from '../utils/constants';

/**
 * Share data
 */
export const useShareDataStore = create((set) => ({
  url: '',
  title: '',
  text: '', // For native share
  imageUrl: '',

  setData: ({ url, title, imageUrl }) => {
    // Add host if not present in url
    if (url && !url.startsWith('http')) {
      url = getConcatenatedUrlParts(process.env.NEXT_PUBLIC_FRONTEND_URL, url);
    }

    set({ url, title, imageUrl, text: title });
  },
}));

/**
 * Save story functionality
 */
export const useSaveStore = create((set, get) => ({
  // User's saved story IDs
  userSavedStoryIds: [],

  // Toggle save status for a story
  toggleSaveStory: async (supabase, storyId) => {
    try {
      const { data, error: authError } = await getCurrentUser(supabase);

      if (authError || !data.user) {
        toast.error('Please log in to manage saved stories');
        return { success: false, data: null, error: authError, message: 'Please log in to manage saved stories' };
      }

      // Check if story is currently saved
      const { error: checkError } = await supabase
        .from('saved_stories')
        .select('*')
        .match({
          user_id: data.user.id,
          story_id: storyId
        })
        .single();

      let response;
      if (checkError && checkError.code === 'PGRST116') {
        // Story is not saved, so save it
        const { error } = await supabase
          .from('saved_stories')
          .insert({
            user_id: data.user.id,
            story_id: storyId
          });

        if (error) {
          response = { success: false, data: null, error, message: 'Failed to save story' };
        } else {
          response = { success: true, data: true, error: null, message: 'Story saved successfully' };
        }
      } else if (!checkError) {
        // Story is saved, so unsave it
        const { error } = await supabase
          .from('saved_stories')
          .delete()
          .match({
            user_id: data.user.id,
            story_id: storyId
          });

        if (error) {
          response = { success: false, data: null, error, message: 'Failed to unsave story' };
        } else {
          response = { success: true, data: false, error: null, message: 'Story unsaved successfully' };
        }
      } else {
        response = { success: false, data: null, error: checkError, message: 'Failed to check save status' };
      }

      if (response.success) {
        // Update user's saved story IDs
        await get().getUserSavedStories(supabase);

        // Show success toast message
        toast.success(response.message);
      } else {
        // Handle error case
        console.error('Error toggling save status:', response.error);
        toast.error(response.message || 'Failed to save/unsave story');
      }

      return response;
    } catch (error) {
      const response = { success: false, data: null, error, message: 'Exception while toggling save status' };
      console.error('Error toggling save status:', error);
      toast.error('Failed to save/unsave story');
      
      return response;
    }
  },

  // Fetch all saved stories for the current user
  getUserSavedStories: async (supabase) => {
    try {
      const { data, error: authError } = await getCurrentUser(supabase);

      if (authError || !data.user) {
        return { success: false, data: [], error: authError, message: 'Please log in to manage saved stories' };
      }

      const { data: savedData, error } = await supabase
        .from('saved_stories')
        .select('story_id')
        .eq('user_id', data.user.id);

      if (error) {
        return { success: false, data: [], error, message: 'Failed to fetch saved stories' };
      }

      // Return just array of story ids
      const storyIds = savedData.map(item => item.story_id);
      set({ userSavedStoryIds: storyIds });
      
      return { success: true, data: storyIds, error: null, message: `Retrieved ${storyIds.length} saved stories` };
    } catch (error) {
      return { success: false, data: [], error, message: 'Exception while fetching saved stories' };
    }
  },

  // Check if a story is saved by ID
  isStorySaved: (storyId) => {
    return get().userSavedStoryIds.includes(storyId);
  },

  // Directly unsave a story without toggling
  unsaveStory: async (supabase, storyId) => {
    try {
      const { data, error: authError } = await getCurrentUser(supabase);

      if (authError || !data.user) {
        toast.error('Please log in to manage saved stories');
        return { success: false, data: null, error: authError, message: 'Please log in to manage saved stories' };
      }

      const { error } = await supabase
        .from('saved_stories')
        .delete()
        .match({
          user_id: data.user.id,
          story_id: storyId
        });

      if (error) {
        const response = { success: false, data: null, error, message: 'Failed to unsave story' };
        console.error('Error unsaving story:', error);
        toast.error(response.message);
        
        return response;
      }

      // Update user's saved story IDs
      await get().getUserSavedStories(supabase);

      // Show success toast message
      const response = { success: true, data: null, error: null, message: 'Story unsaved successfully' };
      toast.success(response.message);
      
      return response;
    } catch (error) {
      const response = { success: false, data: null, error, message: 'Exception while unsaving story' };
      console.error('Error unsaving story:', error);
      toast.error('Failed to unsave story');

      return response;
    }
  }
}));

/**
 * Comments store for managing comments state across components
 */
export const useCommentsStore = create((set, get) => ({
  // Map of storyId -> comments array
  commentsByStory: {},
  // Map of storyId -> loading state
  loadingStates: {},
  // Map of storyId -> error state
  errorStates: {},
  // Map of storyId -> total count (all comments + replies)
  totalCounts: {},
  // Map of storyId -> currently displayed count (comments + replies shown)
  visibleCounts: {},
  // Map of storyId -> target number of items to display (incremented by "load more")
  targetCounts: {},
  // Currently replying to comment ID
  replyingTo: null,

  // Set the comment being replied to
  setReplyingTo: (commentId) => {
    set({ replyingTo: commentId });
  },

  // Fetch comments for a story
  fetchComments: async (supabase, storyId) => {
    if (!storyId) return { success: false, error: 'No story ID provided' };

    set(state => ({
      loadingStates: { ...state.loadingStates, [storyId]: true },
      errorStates: { ...state.errorStates, [storyId]: null }
    }));

    try {
      // Get the current user's ID if authenticated
      const { data: { user } } = await supabase.auth.getUser();
      const currentUserId = user?.id;
      const targetCount = get().targetCounts[storyId] || 5;

      // Build the visibility filter for auth status
      const statusFilter = !currentUserId
        ? 'status.eq.approved'
        : `status.eq.approved,and(user_id.eq.${currentUserId},status.in.(pending,declined))`;

      const applyStatusFilter = (query) => {
        return !currentUserId
          ? query.eq('status', 'approved')
          : query.or(statusFilter);
      };

      // Get total count of ALL comments (top-level + replies) for this story
      const totalQuery = applyStatusFilter(
        supabase.from('comments').select('*', { count: 'exact', head: true }).eq('story_id', storyId)
      );

      // Fetch top-level comments — use targetCount as upper bound (max possible if no replies)
      const topQuery = applyStatusFilter(
        supabase.from('comments').select('*').eq('story_id', storyId).is('reply_to', null)
      );

      // Run total count and top-level queries in parallel
      const [totalResult, topResult] = await Promise.all([
        totalQuery,
        topQuery.order('created_at', { ascending: false }).limit(targetCount)
      ]);

      if (totalResult.error) throw totalResult.error;
      if (topResult.error) throw topResult.error;

      const totalCount = totalResult.count || 0;
      const topData = topResult.data;

      // Fetch all replies for the retrieved top-level comments
      let replyData = [];
      if (topData.length > 0) {
        const parentIds = topData.map(c => c.id);

        const replyQuery = applyStatusFilter(
          supabase.from('comments').select('*').eq('story_id', storyId).in('reply_to', parentIds)
        );

        const { data, error: replyError } = await replyQuery
          .order('created_at', { ascending: true });

        if (replyError) throw replyError;
        replyData = data || [];
      }

      // Group replies by parent
      const repliesByParent = {};
      replyData.forEach(reply => {
        if (!repliesByParent[reply.reply_to]) repliesByParent[reply.reply_to] = [];
        repliesByParent[reply.reply_to].push(reply);
      });

      // Accumulate threads until we reach the target count, truncating replies if needed
      const visibleThreads = [];
      let visibleItemCount = 0;

      for (const comment of topData) {
        if (visibleItemCount >= targetCount) break;

        const threadReplies = repliesByParent[comment.id] || [];
        const remaining = targetCount - visibleItemCount - 1; // -1 for the comment itself
        const visibleReplies = threadReplies.slice(0, remaining);

        visibleThreads.push({ comment, replies: visibleReplies });
        visibleItemCount += 1 + visibleReplies.length;
      }

      // Collect all visible comments for profile fetching
      const allVisibleComments = [];
      visibleThreads.forEach(({ comment, replies }) => {
        allVisibleComments.push(comment, ...replies);
      });

      // Fetch profile data for all visible comment authors
      let profilesMap = {};
      if (allVisibleComments.length > 0) {
        const userIds = [...new Set(allVisibleComments.map(c => c.user_id))];

        if (userIds.length > 0) {
          const { data: profilesData } = await supabase
            .from('profiles_public')
            .select('user_id, avatar_url, full_name')
            .in('user_id', userIds);

          profilesData?.forEach(profile => {
            profilesMap[profile.user_id] = profile;
          });
        }
      }

      // Attach profile data and build final structure
      const topLevelComments = visibleThreads.map(({ comment, replies }) => {
        comment.profiles_public = profilesMap[comment.user_id] || null;
        comment.replies = replies.map(reply => {
          reply.profiles_public = profilesMap[reply.user_id] || null;
          return reply;
        });
        return comment;
      });

      // Update comments and related states
      set(state => ({
        totalCounts: { ...state.totalCounts, [storyId]: totalCount },
        visibleCounts: { ...state.visibleCounts, [storyId]: visibleItemCount },
        commentsByStory: { ...state.commentsByStory, [storyId]: topLevelComments },
        loadingStates: { ...state.loadingStates, [storyId]: false }
      }));

      return { success: true, data: topLevelComments };
    } catch (err) {
      console.error('Error fetching comments:', err);

      set(state => ({
        errorStates: { ...state.errorStates, [storyId]: err.message || 'Failed to load comments' },
        loadingStates: { ...state.loadingStates, [storyId]: false }
      }));

      return { success: false, error: err.message || 'Failed to load comments' };
    }
  },

  // Add a new comment
  addComment: async (supabase, { storyId, userId, content, replyToId }) => {
    if (!storyId || !userId || !content?.trim()) {
      return { success: false, error: 'Missing required fields' };
    }

    try {
      const { error } = await supabase
        .from('comments')
        .insert({
          story_id: storyId,
          user_id: userId,
          content: getStripHtmlTagsStr(content.trim()),
          reply_to: replyToId || null
        });

      if (error) throw error;

      // Wait 2 seconds before refreshing comments, to allow time for the comment to be reviewed by AI
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Refresh comments after adding
      await get().fetchComments(supabase, storyId);

      // Reset replying state after comments are refreshed so the form stays visible while loading
      if (replyToId && get().replyingTo === replyToId) {
        set({ replyingTo: null });
      }

      return { success: true };
    } catch (err) {
      console.error('Error adding comment:', err);
      return { success: false, error: err.message || 'Failed to add comment' };
    }
  },

  // Load more comments — shows 5 more items each time
  loadMoreComments: (storyId) => {
    set(state => ({
      targetCounts: {
        ...state.targetCounts,
        [storyId]: (state.targetCounts[storyId] || 5) + 5
      }
    }));
  },

  // Delete comment
  deleteComment: async (supabase, commentId, storyId) => {
    try {
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      // Refresh comments after deleting
      await get().fetchComments(supabase, storyId);

      return { success: true };
    } catch (err) {
      console.error('Error deleting comment:', err);
      return { success: false, error: err.message || 'Failed to delete comment' };
    }
  },

  // Get comments for a story
  getComments: (storyId) => {
    return get().commentsByStory[storyId] || [];
  },

  // Get loading state for a story
  isLoading: (storyId) => {
    return get().loadingStates[storyId] || false;
  },

  // Get error state for a story
  getError: (storyId) => {
    return get().errorStates[storyId] || null;
  },

  // Get total count for a story
  getTotalCount: (storyId) => {
    return get().totalCounts[storyId] || 0;
  },

  // Get visible count for a story
  getVisibleCount: (storyId) => {
    return get().visibleCounts[storyId] || 5;
  },

  // Check if more comments can be loaded
  canLoadMore: (storyId) => {
    return get().getTotalCount(storyId) > get().getVisibleCount(storyId);
  },

  // Fetch all comments by the current user
  fetchUserComments: async (supabase, userId) => {
    if (!userId) return { success: false, error: 'No user ID provided' };

    try {
      // Fetch comments made by the user with profiles joined
      // Note: We can't join with stories table as it doesn't exist in the database
      const { data, error } = await supabase
        .from('comments')
        .select(`*`)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, data };
    } catch (err) {
      console.error('Error fetching user comments:', err);
      return { success: false, error: err.message || 'Failed to load user comments' };
    }
  }
}));

/**
 * Auth Dialog store for managing login dialog state globally
 */
export const useAuthDialogStore = create((set) => ({
  isLoginDialogOpen: false,
  view: 'sign_in', // 'sign_in' or 'sign_up'
  
  openLoginDialog: (view = 'sign_in') => {
    // Handle case where openLoginDialog is called as onClick handler without params
    const finalView = typeof view === 'string' ? view : 'sign_in';
    set({ isLoginDialogOpen: true, view: finalView });
  },
  openSignupDialog: () => set({ isLoginDialogOpen: true, view: 'sign_up' }),
  closeLoginDialog: () => set({ isLoginDialogOpen: false }),
  setLoginDialogOpen: (isOpen) => set({ isLoginDialogOpen: isOpen }),
  setView: (view) => set({ view }),
}));

/**
 * Article Limit store for managing user article view limits
 */
export const useArticleLimitStore = create((set, get) => ({
  // State
  viewCount: 0,
  fingerprint: null,
  isLoading: false,
  isInitialized: false,
  lastReset: null,
  hasExceededLimitCached: false,
  
  // Getters
  hasExceededLimit: () => {
    const state = get();
    // Check sessionStorage flag first
    if (typeof window !== 'undefined') {
      const cachedFlag = sessionStorage.getItem('article_limit_exceeded');
      if (cachedFlag === 'true') {
        return true;
      }
    }
    return state.viewCount > ARTICLE_LIMIT_COUNT;
  },
  getRemainingViews: () => Math.max(0, ARTICLE_LIMIT_COUNT - get().viewCount),
  getViewCount: () => get().viewCount,
  isAtLimit: () => get().viewCount >= ARTICLE_LIMIT_COUNT,
  
  // Helper function to get article view count
  getArticleViewCount: async (supabase, fingerprint) => {
    if (!fingerprint) return 0;
    
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    
    try {
      const { count, error } = await supabase
        .from('article_views')
        .select('*', { count: 'exact', head: true })
        .eq('fingerprint', fingerprint)
        .gte('viewed_at', oneWeekAgo.toISOString());
        
      if (error) {
        console.error('Error getting article view count:', error);
        return 0;
      }
      
      return count || 0;
    } catch (error) {
      console.error('Error getting article view count:', error);
      return 0;
    }
  },
  
  // Helper function to record article view
  recordArticleView: async (supabase, fingerprint, articleSlug) => {
    if (!fingerprint || !articleSlug) return false;
    
    try {
      const { error } = await supabase
        .from('article_views')
        .upsert({ 
          fingerprint, 
          article_slug: articleSlug,
          viewed_at: new Date().toISOString()
        }, {
          onConflict: 'fingerprint,article_slug'
        });
        
      if (error) {
        console.error('Error recording article view:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error recording article view:', error);
      return false;
    }
  },
  
  // Initialize fingerprint and load current count
  initialize: async (supabase) => {
    const state = get();
    if (state.isInitialized || state.isLoading) {
      return state.fingerprint;
    }
    
    set({ isLoading: true });
    
    try {
      // Check sessionStorage flag first
      if (typeof window !== 'undefined') {
        const cachedFlag = sessionStorage.getItem('article_limit_exceeded');
        if (cachedFlag === 'true') {
          // User has already exceeded limit, no need to check database
          set({ 
            isLoading: false, 
            isInitialized: true,
            hasExceededLimitCached: true,
            viewCount: ARTICLE_LIMIT_COUNT + 1 // At this stage, the user has exceeded the limit by 1 article
          });
          return null;
        }
      }
      
      // Generate fingerprint
      const fingerprint = await generateFingerprint();
      
      // Get current view count from database
      const viewCount = await get().getArticleViewCount(supabase, fingerprint);
      
      // Check if user has exceeded limit and cache the flag
      if (viewCount > ARTICLE_LIMIT_COUNT) {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('article_limit_exceeded', 'true');
        }
        set({ 
          fingerprint, 
          viewCount, 
          isLoading: false, 
          isInitialized: true,
          hasExceededLimitCached: true
        });
      } else {
        set({ 
          fingerprint, 
          viewCount, 
          isLoading: false, 
          isInitialized: true 
        });
      }
      
      return fingerprint;
    } catch (error) {
      console.error('Error initializing article limit store:', error);
      set({ isLoading: false, isInitialized: true });
      return null;
    }
  },
  
  // Track a new article view
  trackArticleView: async (supabase, articleSlug) => {
    const state = get();
    
    // Check sessionStorage flag first - if exceeded, don't track
    if (typeof window !== 'undefined') {
      const cachedFlag = sessionStorage.getItem('article_limit_exceeded');
      if (cachedFlag === 'true') {
        // console.log('Article limit already exceeded, skipping tracking');
        return false;
      }
    }
    
    const { fingerprint } = state;
    
    if (!fingerprint || !articleSlug) {
      console.warn('Cannot track article view: missing fingerprint or slug');
      return false;
    }
    
    try {
      // Record the view in database
      const success = await get().recordArticleView(supabase, fingerprint, articleSlug);
      
      if (success) {
        // Refresh the count from database to get accurate count
        const newCount = await get().getArticleViewCount(supabase, fingerprint);
        
        // Check if user has now exceeded limit and cache the flag
        if (newCount > ARTICLE_LIMIT_COUNT) {
          if (typeof window !== 'undefined') {
            sessionStorage.setItem('article_limit_exceeded', 'true');
          }
          set({ 
            viewCount: newCount,
            hasExceededLimitCached: true
          });
          console.log(`Article limit exceeded at count: ${newCount}`);
        } else {
          set({ viewCount: newCount });
        }
        
        // Log for debugging
        console.log(`Article view tracked: ${articleSlug}, new count: ${newCount}`);
      }
      
      return success;
    } catch (error) {
      console.error('Error tracking article view:', error);
      return false;
    }
  },
  
  // Force refresh count from database
  refreshCount: async (supabase) => {
    const { fingerprint } = get();
    
    if (!fingerprint) {
      return 0;
    }
    
    try {
      const newCount = await get().getArticleViewCount(supabase, fingerprint);
      set({ viewCount: newCount });
      return newCount;
    } catch (error) {
      console.error('Error refreshing article count:', error);
      return get().viewCount;
    }
  },
  
  // Reset store state (for testing/debugging)
  reset: () => {
    // Clear sessionStorage flag
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('article_limit_exceeded');
    }
    
    set({
      viewCount: 0,
      fingerprint: null,
      isLoading: false,
      isInitialized: false,
      lastReset: null,
      hasExceededLimitCached: false
    });
  }
}));
